'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, forwardRef } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Calendar, Clock, CalendarDays } from 'lucide-react';
import { cn } from '../utils/cn';
import 'react-datepicker/dist/react-datepicker.css';
// Custom Input Component
const CustomInput = forwardRef(({ value, onClick, placeholder, error, darkMode, variant = 'date', onChange, ...props }, ref) => {
    const getIcon = () => {
        switch (variant) {
            case 'time':
                return _jsx(Clock, { className: "h-4 w-4" });
            case 'datetime':
                return _jsx(CalendarDays, { className: "h-4 w-4" });
            default:
                return _jsx(Calendar, { className: "h-4 w-4" });
        }
    };
    return (_jsxs("div", { className: "relative w-full", children: [_jsx("input", { ...props, ref: ref, value: value, onClick: onClick, onChange: onChange, placeholder: placeholder, className: cn("h-10 w-full px-3 py-2 pl-10 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 cursor-text transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-500", error
                    ? "border-red-500 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-400"
                    : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:border-gray-400 dark:hover:border-gray-500") }), _jsx("div", { className: "absolute inset-y-0 left-0 pl-3 flex items-center cursor-pointer", onClick: onClick, children: _jsx("div", { className: cn("text-gray-400 dark:text-gray-400", error && "text-red-500 dark:text-red-400"), children: getIcon() }) })] }));
});
CustomInput.displayName = 'CustomInput';
export function DatePicker({ label, error, helperText, required, className, darkMode, variant = 'date', value, onChange, ...props }) {
    // Auto-detect dark mode from document if not explicitly set
    const [isDarkMode, setIsDarkMode] = useState(darkMode ?? (typeof window !== 'undefined' && document.documentElement.classList.contains('dark')));
    const [selectedDate, setSelectedDate] = useState(null);
    // Listen for dark mode changes
    useEffect(() => {
        if (darkMode !== undefined) {
            setIsDarkMode(darkMode);
            return;
        }
        if (typeof window === 'undefined')
            return;
        const updateDarkMode = () => {
            setIsDarkMode(document.documentElement.classList.contains('dark'));
        };
        // Initial check
        updateDarkMode();
        // Watch for class changes on documentElement
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    updateDarkMode();
                }
            });
        });
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });
        return () => observer.disconnect();
    }, [darkMode]);
    // Handle value prop changes
    useEffect(() => {
        if (value === null || value === undefined) {
            setSelectedDate(null);
        }
        else if (value instanceof Date) {
            setSelectedDate(value);
        }
        else if (typeof value === 'string') {
            const date = new Date(value);
            setSelectedDate(isNaN(date.getTime()) ? null : date);
        }
    }, [value]);
    const handleDateChange = (date) => {
        setSelectedDate(date);
        onChange?.(date);
    };
    const getDatePickerProps = () => {
        const baseProps = {
            selected: selectedDate,
            onChange: handleDateChange,
            customInput: (_jsx(CustomInput, { error: error, darkMode: isDarkMode, variant: variant })),
            calendarClassName: cn("react-datepicker-custom", isDarkMode ? "react-datepicker-dark" : "react-datepicker-light"),
            popperClassName: cn("react-datepicker-popper-custom", isDarkMode ? "react-datepicker-popper-dark" : "react-datepicker-popper-light"),
            // Enable manual input
            allowSameDay: true,
            autoComplete: "off",
            // Use month/year picker mode (like native date picker)
            showMonthYearPicker: false,
            showYearPicker: false,
            // Enable clicking on month/year to open picker
            showPopperArrow: false,
            // Year range for the year picker
            minDate: new Date(1900, 0, 1),
            maxDate: new Date(2100, 11, 31),
            ...props
        };
        switch (variant) {
            case 'time':
                return {
                    ...baseProps,
                    showTimeSelect: true,
                    showTimeSelectOnly: true,
                    timeIntervals: 15,
                    timeCaption: "Time",
                    dateFormat: "HH:mm",
                    placeholderText: "Select time...",
                    showMonthDropdown: false,
                    showYearDropdown: false,
                };
            case 'datetime':
                return {
                    ...baseProps,
                    showTimeSelect: true,
                    timeFormat: "HH:mm",
                    timeIntervals: 15,
                    dateFormat: "yyyy-MM-dd HH:mm",
                    placeholderText: "Select date and time..."
                };
            default: // 'date'
                return {
                    ...baseProps,
                    dateFormat: "yyyy-MM-dd",
                    placeholderText: "Select date..."
                };
        }
    };
    return (_jsxs("div", { className: cn("space-y-1", className), children: [label && (_jsxs("label", { className: "block text-sm font-medium text-gray-700 dark:text-gray-200", children: [label, required && _jsx("span", { className: "text-red-500 ml-1", children: "*" })] })), _jsx(ReactDatePicker, { ...getDatePickerProps() }), error && (_jsx("p", { className: "text-sm text-red-600 dark:text-red-400", children: error })), helperText && !error && (_jsx("p", { className: "text-sm text-gray-500 dark:text-gray-400", children: helperText })), _jsx("style", { dangerouslySetInnerHTML: { __html: `
        /* Light Theme */
        .react-datepicker-light .react-datepicker {
          background-color: var(--bg-primary) !important;
          border: 1px solid var(--border-secondary) !important;
          color: var(--text-primary) !important;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
        }
        
        .react-datepicker-light .react-datepicker__header {
          background-color: var(--bg-secondary) !important;
          border-bottom: 1px solid var(--border-secondary) !important;
        }
        
        .react-datepicker-light .react-datepicker__current-month,
        .react-datepicker-light .react-datepicker-time__header {
          color: var(--text-primary) !important;
        }
        
        .react-datepicker-light .react-datepicker__day-name,
        .react-datepicker-light .react-datepicker__day,
        .react-datepicker-light .react-datepicker__time-name {
          color: var(--text-secondary) !important;
        }
        
        .react-datepicker-light .react-datepicker__day:hover {
          background-color: var(--color-primary-100) !important;
          color: var(--color-primary-800) !important;
        }
        
        .react-datepicker-light .react-datepicker__day--selected,
        .react-datepicker-light .react-datepicker__day--keyboard-selected {
          background-color: var(--color-primary-600) !important;
          color: white !important;
        }
        
        .react-datepicker-light .react-datepicker__day--today {
          background-color: var(--color-primary-100) !important;
          color: var(--color-primary-800) !important;
          font-weight: bold !important;
        }

        /* Dark Theme - More specific selectors */
        .react-datepicker-popper-dark .react-datepicker,
        .react-datepicker-dark .react-datepicker {
          background-color: var(--bg-secondary) !important;
          border: 1px solid var(--border-primary) !important;
          color: var(--text-primary) !important;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
        }
        
        .react-datepicker-popper-dark .react-datepicker__header,
        .react-datepicker-dark .react-datepicker__header {
          background-color: var(--bg-tertiary) !important;
          border-bottom: 1px solid var(--border-primary) !important;
        }
        
        .react-datepicker-popper-dark .react-datepicker__current-month,
        .react-datepicker-popper-dark .react-datepicker-time__header,
        .react-datepicker-dark .react-datepicker__current-month,
        .react-datepicker-dark .react-datepicker-time__header {
          color: var(--text-primary) !important;
        }
        
        .react-datepicker-popper-dark .react-datepicker__day-name,
        .react-datepicker-popper-dark .react-datepicker__day,
        .react-datepicker-popper-dark .react-datepicker__time-name,
        .react-datepicker-dark .react-datepicker__day-name,
        .react-datepicker-dark .react-datepicker__day,
        .react-datepicker-dark .react-datepicker__time-name {
          color: var(--text-secondary) !important;
        }
        
        .react-datepicker-popper-dark .react-datepicker__day:hover,
        .react-datepicker-dark .react-datepicker__day:hover {
          background-color: var(--bg-tertiary) !important;
          color: var(--text-primary) !important;
        }
        
        .react-datepicker-popper-dark .react-datepicker__day--selected,
        .react-datepicker-popper-dark .react-datepicker__day--keyboard-selected,
        .react-datepicker-dark .react-datepicker__day--selected,
        .react-datepicker-dark .react-datepicker__day--keyboard-selected {
          background-color: var(--color-primary-600) !important;
          color: white !important;
        }
        
        .react-datepicker-popper-dark .react-datepicker__day--today,
        .react-datepicker-dark .react-datepicker__day--today {
          background-color: var(--color-primary-800) !important;
          color: var(--color-primary-200) !important;
          font-weight: bold !important;
        }
        
        .react-datepicker-popper-dark .react-datepicker__time-container,
        .react-datepicker-dark .react-datepicker__time-container {
          border-left: 1px solid var(--border-primary) !important;
          border-right: 1px solid var(--border-primary) !important;
          border-bottom: 1px solid var(--border-primary) !important;
          background-color: var(--bg-secondary) !important;
        }
        
        .react-datepicker-popper-dark .react-datepicker__time-list,
        .react-datepicker-dark .react-datepicker__time-list {
          background-color: var(--bg-secondary) !important;
          border-right: 1px solid var(--border-primary) !important;
        }
        
        .react-datepicker-popper-dark .react-datepicker__time-list-item,
        .react-datepicker-dark .react-datepicker__time-list-item {
          color: var(--text-secondary) !important;
        }
        
        .react-datepicker-popper-dark .react-datepicker__time-list-item:hover,
        .react-datepicker-dark .react-datepicker__time-list-item:hover {
          background-color: var(--bg-tertiary) !important;
          color: var(--text-primary) !important;
        }
        
        .react-datepicker-popper-dark .react-datepicker__time-list-item--selected,
        .react-datepicker-dark .react-datepicker__time-list-item--selected {
          background-color: var(--color-primary-600) !important;
          color: white !important;
        }

        /* Light theme time list */
        .react-datepicker-light .react-datepicker__time-container {
          border-left: 1px solid var(--border-secondary) !important;
          border-right: 1px solid var(--border-secondary) !important;
          border-bottom: 1px solid var(--border-secondary) !important;
          background-color: var(--bg-primary) !important;
        }
        
        .react-datepicker-light .react-datepicker__time-list {
          background-color: var(--bg-primary) !important;
          border-right: 1px solid var(--border-secondary) !important;
        }
        
        .react-datepicker-light .react-datepicker__time-list-item {
          color: var(--text-secondary) !important;
        }
        
        .react-datepicker-light .react-datepicker__time-list-item:hover {
          background-color: var(--bg-secondary) !important;
          color: var(--text-primary) !important;
        }
        
        .react-datepicker-light .react-datepicker__time-list-item--selected {
          background-color: var(--color-primary-600) !important;
          color: white !important;
        }

        /* Global dark mode styles for when datepicker is in a dark container */
        .dark .react-datepicker,
        [data-theme="dark"] .react-datepicker {
          background-color: var(--bg-secondary) !important;
          border: 1px solid var(--border-primary) !important;
          color: var(--text-primary) !important;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
        }
        
        .dark .react-datepicker__header,
        [data-theme="dark"] .react-datepicker__header {
          background-color: var(--bg-tertiary) !important;
          border-bottom: 1px solid var(--border-primary) !important;
        }
        
        .dark .react-datepicker__current-month,
        .dark .react-datepicker-time__header,
        [data-theme="dark"] .react-datepicker__current-month,
        [data-theme="dark"] .react-datepicker-time__header {
          color: var(--text-primary) !important;
        }
        
        .dark .react-datepicker__day-name,
        .dark .react-datepicker__day,
        .dark .react-datepicker__time-name,
        [data-theme="dark"] .react-datepicker__day-name,
        [data-theme="dark"] .react-datepicker__day,
        [data-theme="dark"] .react-datepicker__time-name {
          color: var(--text-secondary) !important;
        }
        
        .dark .react-datepicker__day:hover,
        [data-theme="dark"] .react-datepicker__day:hover {
          background-color: var(--bg-tertiary) !important;
          color: var(--text-primary) !important;
        }
        
        .dark .react-datepicker__day--selected,
        .dark .react-datepicker__day--keyboard-selected,
        [data-theme="dark"] .react-datepicker__day--selected,
        [data-theme="dark"] .react-datepicker__day--keyboard-selected {
          background-color: var(--color-primary-600) !important;
          color: white !important;
        }
        
        .dark .react-datepicker__day--today,
        [data-theme="dark"] .react-datepicker__day--today {
          background-color: var(--color-primary-800) !important;
          color: var(--color-primary-200) !important;
          font-weight: bold !important;
        }
        
        .dark .react-datepicker__time-container,
        [data-theme="dark"] .react-datepicker__time-container {
          border-left: 1px solid var(--border-primary) !important;
          border-right: 1px solid var(--border-primary) !important;
          border-bottom: 1px solid var(--border-primary) !important;
          background-color: var(--bg-secondary) !important;
        }
        
        .dark .react-datepicker__time-list,
        [data-theme="dark"] .react-datepicker__time-list {
          background-color: var(--bg-secondary) !important;
          border-right: 1px solid var(--border-primary) !important;
        }
        
        .dark .react-datepicker__time-list-item,
        [data-theme="dark"] .react-datepicker__time-list-item {
          color: var(--text-secondary) !important;
        }
        
        .dark .react-datepicker__time-list-item:hover,
        [data-theme="dark"] .react-datepicker__time-list-item:hover {
          background-color: var(--bg-tertiary) !important;
          color: var(--text-primary) !important;
        }
        
        .dark .react-datepicker__time-list-item--selected,
        [data-theme="dark"] .react-datepicker__time-list-item--selected {
          background-color: var(--color-primary-600) !important;
          color: white !important;
        }

        /* Global time list styles - Default (Light) */
        .react-datepicker__time-container {
          border-left: 1px solid var(--border-secondary) !important;
          border-right: 1px solid var(--border-secondary) !important;
          border-bottom: 1px solid var(--border-secondary) !important;
          border-top: none !important;
          background-color: var(--bg-primary) !important;
        }
        
        .react-datepicker__time-list {
          background-color: var(--bg-primary) !important;
          border: none !important;
        }
        
        .react-datepicker__time-box {
          border-right: 1px solid var(--border-secondary) !important;
        }
        
        /* Ensure time header matches */
        .react-datepicker__time-container .react-datepicker-time__header {
          background-color: var(--bg-secondary) !important;
          color: var(--text-primary) !important;
          border-bottom: 1px solid var(--border-secondary) !important;
        }
        
        .react-datepicker__time-list-item {
          color: var(--text-secondary) !important;
        }
        
        .react-datepicker__time-list-item:hover {
          background-color: var(--bg-secondary) !important;
          color: var(--text-primary) !important;
        }
        
        .react-datepicker__time-list-item--selected {
          background-color: var(--color-primary-600) !important;
          color: white !important;
        }

        /* Override for dark mode - More specific selectors */
        html.dark .react-datepicker__time-container,
        body.dark .react-datepicker__time-container,
        .dark .react-datepicker__time-container,
        [data-theme="dark"] .react-datepicker__time-container,
        :root.dark .react-datepicker__time-container {
          border-left: 1px solid var(--border-primary) !important;
          border-right: 1px solid var(--border-primary) !important;
          border-bottom: 1px solid var(--border-primary) !important;
          border-top: none !important;
          background-color: var(--bg-secondary) !important;
        }
        
        html.dark .react-datepicker__time-list,
        body.dark .react-datepicker__time-list,
        .dark .react-datepicker__time-list,
        [data-theme="dark"] .react-datepicker__time-list,
        :root.dark .react-datepicker__time-list {
          background-color: var(--bg-secondary) !important;
          border: none !important;
        }
        
        html.dark .react-datepicker__time-box,
        body.dark .react-datepicker__time-box,
        .dark .react-datepicker__time-box,
        [data-theme="dark"] .react-datepicker__time-box,
        :root.dark .react-datepicker__time-box {
          border-right: 1px solid var(--border-primary) !important;
        }
        
        /* Dark mode time header */
        html.dark .react-datepicker__time-container .react-datepicker-time__header,
        body.dark .react-datepicker__time-container .react-datepicker-time__header,
        .dark .react-datepicker__time-container .react-datepicker-time__header,
        [data-theme="dark"] .react-datepicker__time-container .react-datepicker-time__header,
        :root.dark .react-datepicker__time-container .react-datepicker-time__header {
          background-color: var(--bg-tertiary) !important;
          color: var(--text-primary) !important;
          border-bottom: 1px solid var(--border-primary) !important;
        }
        
        /* Dark mode time list items - Multiple selectors for better coverage */
        html.dark .react-datepicker__time-list-item,
        body.dark .react-datepicker__time-list-item,
        :root.dark .react-datepicker__time-list-item,
        .dark .react-datepicker__time-list-item,
        [data-theme="dark"] .react-datepicker__time-list-item {
          color: var(--text-secondary) !important;
          background-color: transparent !important;
        }
        
        html.dark .react-datepicker__time-list-item:hover,
        body.dark .react-datepicker__time-list-item:hover,
        :root.dark .react-datepicker__time-list-item:hover,
        .dark .react-datepicker__time-list-item:hover,
        [data-theme="dark"] .react-datepicker__time-list-item:hover {
          background-color: var(--bg-tertiary) !important;
          color: var(--text-primary) !important;
        }
        
        html.dark .react-datepicker__time-list-item--selected,
        body.dark .react-datepicker__time-list-item--selected,
        :root.dark .react-datepicker__time-list-item--selected,
        .dark .react-datepicker__time-list-item--selected,
        [data-theme="dark"] .react-datepicker__time-list-item--selected {
          background-color: var(--color-primary-600) !important;
          color: white !important;
        }

        /* Navigation buttons - Light theme */
        .react-datepicker-light .react-datepicker__navigation {
          border: none !important;
          background: none !important;
        }
        
        .react-datepicker-light .react-datepicker__navigation--previous::before {
          border-right-color: var(--text-tertiary) !important;
          border-top-color: transparent !important;
          border-bottom-color: transparent !important;
          border-left-color: transparent !important;
        }
        
        .react-datepicker-light .react-datepicker__navigation--next::before {
          border-left-color: var(--text-tertiary) !important;
          border-top-color: transparent !important;
          border-bottom-color: transparent !important;
          border-right-color: transparent !important;
        }
        
        .react-datepicker-light .react-datepicker__navigation:hover::before {
          border-color: var(--text-primary) !important;
        }
        
        .react-datepicker-light .react-datepicker__navigation--previous:hover::before {
          border-right-color: var(--text-primary) !important;
          border-top-color: transparent !important;
          border-bottom-color: transparent !important;
          border-left-color: transparent !important;
        }
        
        .react-datepicker-light .react-datepicker__navigation--next:hover::before {
          border-left-color: var(--text-primary) !important;
          border-top-color: transparent !important;
          border-bottom-color: transparent !important;
          border-right-color: transparent !important;
        }

        /* Navigation buttons - Dark theme */
        .react-datepicker-popper-dark .react-datepicker__navigation,
        .react-datepicker-dark .react-datepicker__navigation,
        .dark .react-datepicker__navigation,
        [data-theme="dark"] .react-datepicker__navigation {
          border: none !important;
          background: none !important;
        }
        
        .react-datepicker-popper-dark .react-datepicker__navigation--previous::before,
        .react-datepicker-dark .react-datepicker__navigation--previous::before,
        .dark .react-datepicker__navigation--previous::before,
        [data-theme="dark"] .react-datepicker__navigation--previous::before {
          border-right-color: var(--text-secondary) !important;
          border-top-color: transparent !important;
          border-bottom-color: transparent !important;
          border-left-color: transparent !important;
        }
        
        .react-datepicker-popper-dark .react-datepicker__navigation--next::before,
        .react-datepicker-dark .react-datepicker__navigation--next::before,
        .dark .react-datepicker__navigation--next::before,
        [data-theme="dark"] .react-datepicker__navigation--next::before {
          border-left-color: var(--text-secondary) !important;
          border-top-color: transparent !important;
          border-bottom-color: transparent !important;
          border-right-color: transparent !important;
        }
        
        .react-datepicker-popper-dark .react-datepicker__navigation--previous:hover::before,
        .react-datepicker-dark .react-datepicker__navigation--previous:hover::before,
        .dark .react-datepicker__navigation--previous:hover::before,
        [data-theme="dark"] .react-datepicker__navigation--previous:hover::before {
          border-right-color: var(--text-primary) !important;
          border-top-color: transparent !important;
          border-bottom-color: transparent !important;
          border-left-color: transparent !important;
        }
        
        .react-datepicker-popper-dark .react-datepicker__navigation--next:hover::before,
        .react-datepicker-dark .react-datepicker__navigation--next:hover::before,
        .dark .react-datepicker__navigation--next:hover::before,
        [data-theme="dark"] .react-datepicker__navigation--next:hover::before {
          border-left-color: var(--text-primary) !important;
          border-top-color: transparent !important;
          border-bottom-color: transparent !important;
          border-right-color: transparent !important;
        }

        /* Input wrapper styles */
        .react-datepicker-wrapper {
          width: 100% !important;
          display: block !important;
        }

        .react-datepicker__input-container {
          width: 100% !important;
          display: block !important;
        }

        /* Clear button positioning - prevent overlap with date text */
        .react-datepicker__close-icon {
          right: 8px !important;
          padding: 0 !important;
        }

        .react-datepicker__close-icon::after {
          background-color: var(--text-tertiary) !important;
          font-size: 14px !important;
          height: 18px !important;
          width: 18px !important;
          padding: 0 !important;
          line-height: 16px !important;
        }

        .react-datepicker__close-icon:hover::after {
          background-color: var(--text-secondary) !important;
        }

        /* Dark mode clear button */
        .dark .react-datepicker__close-icon::after,
        [data-theme="dark"] .react-datepicker__close-icon::after {
          background-color: var(--text-secondary) !important;
        }

        .dark .react-datepicker__close-icon:hover::after,
        [data-theme="dark"] .react-datepicker__close-icon:hover::after {
          background-color: var(--text-primary) !important;
        }

        /* Add right padding to input when clearable to prevent text overlap with X button */
        .react-datepicker__input-container input {
          padding-right: 32px !important;
        }
        
        /* Popper positioning */
        .react-datepicker-popper-custom {
          z-index: 50 !important;
        }

        /* Month and Year Dropdowns - Light Theme */
        .react-datepicker-light .react-datepicker__month-dropdown,
        .react-datepicker-light .react-datepicker__year-dropdown {
          background-color: var(--bg-primary) !important;
          border: 1px solid var(--border-secondary) !important;
          color: var(--text-primary) !important;
        }

        .react-datepicker-light .react-datepicker__month-option,
        .react-datepicker-light .react-datepicker__year-option {
          color: var(--text-primary) !important;
          padding: 0.5rem !important;
        }

        .react-datepicker-light .react-datepicker__month-option:hover,
        .react-datepicker-light .react-datepicker__year-option:hover {
          background-color: var(--bg-secondary) !important;
        }

        .react-datepicker-light .react-datepicker__month-option--selected,
        .react-datepicker-light .react-datepicker__year-option--selected {
          background-color: var(--color-primary-600) !important;
          color: white !important;
        }

        .react-datepicker-light .react-datepicker__month-read-view--selected-month,
        .react-datepicker-light .react-datepicker__year-read-view--selected-year {
          color: var(--text-primary) !important;
        }

        /* Hover effect for year/month read view - Light */
        .react-datepicker-light .react-datepicker__year-read-view:hover,
        .react-datepicker-light .react-datepicker__month-read-view:hover {
          background-color: var(--bg-secondary) !important;
          border-color: var(--border-secondary) !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
        }

        /* Active/Open state - Light */
        .react-datepicker-light .react-datepicker__year-read-view--selected-year,
        .react-datepicker-light .react-datepicker__month-read-view--selected-month {
          background-color: var(--color-primary-50) !important;
          border-color: var(--color-primary-300) !important;
          color: var(--color-primary-700) !important;
        }

        /* Dropdown arrow styling - Light */
        .react-datepicker-light .react-datepicker__year-read-view--down-arrow,
        .react-datepicker-light .react-datepicker__month-read-view--down-arrow {
          border-top-color: var(--text-secondary) !important;
        }

        /* Month and Year Dropdowns - Dark Theme */
        .react-datepicker-popper-dark .react-datepicker__month-dropdown,
        .react-datepicker-popper-dark .react-datepicker__year-dropdown,
        .react-datepicker-dark .react-datepicker__month-dropdown,
        .react-datepicker-dark .react-datepicker__year-dropdown,
        .dark .react-datepicker__month-dropdown,
        .dark .react-datepicker__year-dropdown,
        [data-theme="dark"] .react-datepicker__month-dropdown,
        [data-theme="dark"] .react-datepicker__year-dropdown {
          background-color: var(--bg-secondary) !important;
          border: 1px solid var(--border-primary) !important;
          color: var(--text-primary) !important;
        }

        .react-datepicker-popper-dark .react-datepicker__month-option,
        .react-datepicker-popper-dark .react-datepicker__year-option,
        .react-datepicker-dark .react-datepicker__month-option,
        .react-datepicker-dark .react-datepicker__year-option,
        .dark .react-datepicker__month-option,
        .dark .react-datepicker__year-option,
        [data-theme="dark"] .react-datepicker__month-option,
        [data-theme="dark"] .react-datepicker__year-option {
          color: var(--text-primary) !important;
          padding: 0.5rem !important;
        }

        .react-datepicker-popper-dark .react-datepicker__month-option:hover,
        .react-datepicker-popper-dark .react-datepicker__year-option:hover,
        .react-datepicker-dark .react-datepicker__month-option:hover,
        .react-datepicker-dark .react-datepicker__year-option:hover,
        .dark .react-datepicker__month-option:hover,
        .dark .react-datepicker__year-option:hover,
        [data-theme="dark"] .react-datepicker__month-option:hover,
        [data-theme="dark"] .react-datepicker__year-option:hover {
          background-color: var(--bg-tertiary) !important;
        }

        .react-datepicker-popper-dark .react-datepicker__month-option--selected,
        .react-datepicker-popper-dark .react-datepicker__year-option--selected,
        .react-datepicker-dark .react-datepicker__month-option--selected,
        .react-datepicker-dark .react-datepicker__year-option--selected,
        .dark .react-datepicker__month-option--selected,
        .dark .react-datepicker__year-option--selected,
        [data-theme="dark"] .react-datepicker__month-option--selected,
        [data-theme="dark"] .react-datepicker__year-option--selected {
          background-color: var(--color-primary-600) !important;
          color: white !important;
        }

        .react-datepicker-popper-dark .react-datepicker__month-read-view--selected-month,
        .react-datepicker-popper-dark .react-datepicker__year-read-view--selected-year,
        .react-datepicker-dark .react-datepicker__month-read-view--selected-month,
        .react-datepicker-dark .react-datepicker__year-read-view--selected-year,
        .dark .react-datepicker__month-read-view--selected-month,
        .dark .react-datepicker__year-read-view--selected-year,
        [data-theme="dark"] .react-datepicker__month-read-view--selected-month,
        [data-theme="dark"] .react-datepicker__year-read-view--selected-year {
          color: var(--text-primary) !important;
        }

        /* Hover effect for year/month read view - Dark */
        .react-datepicker-popper-dark .react-datepicker__year-read-view:hover,
        .react-datepicker-popper-dark .react-datepicker__month-read-view:hover,
        .react-datepicker-dark .react-datepicker__year-read-view:hover,
        .react-datepicker-dark .react-datepicker__month-read-view:hover,
        .dark .react-datepicker__year-read-view:hover,
        .dark .react-datepicker__month-read-view:hover,
        [data-theme="dark"] .react-datepicker__year-read-view:hover,
        [data-theme="dark"] .react-datepicker__month-read-view:hover {
          background-color: var(--bg-tertiary) !important;
          border-color: var(--border-primary) !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
        }

        /* Active/Open state - Dark */
        .react-datepicker-popper-dark .react-datepicker__year-read-view--selected-year,
        .react-datepicker-popper-dark .react-datepicker__month-read-view--selected-month,
        .react-datepicker-dark .react-datepicker__year-read-view--selected-year,
        .react-datepicker-dark .react-datepicker__month-read-view--selected-month,
        .dark .react-datepicker__year-read-view--selected-year,
        .dark .react-datepicker__month-read-view--selected-month,
        [data-theme="dark"] .react-datepicker__year-read-view--selected-year,
        [data-theme="dark"] .react-datepicker__month-read-view--selected-month {
          background-color: var(--color-primary-900) !important;
          border-color: var(--color-primary-600) !important;
          color: var(--color-primary-200) !important;
        }

        /* Dropdown arrow styling - Dark */
        .react-datepicker-popper-dark .react-datepicker__year-read-view--down-arrow,
        .react-datepicker-popper-dark .react-datepicker__month-read-view--down-arrow,
        .react-datepicker-dark .react-datepicker__year-read-view--down-arrow,
        .react-datepicker-dark .react-datepicker__month-read-view--down-arrow,
        .dark .react-datepicker__year-read-view--down-arrow,
        .dark .react-datepicker__month-read-view--down-arrow,
        [data-theme="dark"] .react-datepicker__year-read-view--down-arrow,
        [data-theme="dark"] .react-datepicker__month-read-view--down-arrow {
          border-top-color: var(--text-secondary) !important;
        }

        /* Scrollable year dropdown - Better UX with scroll mode */
        .react-datepicker__year-dropdown--scrollable {
          max-height: 300px !important;
          overflow-y: auto !important;
          position: absolute !important;
          top: 30px !important;
          left: 0 !important;
          right: 0 !important;
          z-index: 100 !important;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
          border-radius: 0.5rem !important;
          padding: 0.5rem 0 !important;
          min-width: 120px !important;
        }

        /* Month dropdown - consistent with year dropdown */
        .react-datepicker__month-dropdown {
          max-height: 300px !important;
          overflow-y: auto !important;
          position: absolute !important;
          top: 30px !important;
          left: 0 !important;
          right: 0 !important;
          z-index: 100 !important;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
          border-radius: 0.5rem !important;
          padding: 0.5rem 0 !important;
          min-width: 120px !important;
        }

        /* Year and Month option styling - Enhanced */
        .react-datepicker__year-option,
        .react-datepicker__month-option {
          padding: 0.625rem 1rem !important;
          cursor: pointer !important;
          transition: all 0.15s ease-in-out !important;
          margin: 0 0.25rem !important;
          border-radius: 0.375rem !important;
          font-size: 0.875rem !important;
          line-height: 1.25rem !important;
        }

        /* First and last item spacing */
        .react-datepicker__year-option:first-child,
        .react-datepicker__month-option:first-child {
          margin-top: 0.25rem !important;
        }

        .react-datepicker__year-option:last-child,
        .react-datepicker__month-option:last-child {
          margin-bottom: 0.25rem !important;
        }

        /* Dropdown container positioning */
        .react-datepicker__year-dropdown-container,
        .react-datepicker__month-dropdown-container {
          position: relative !important;
          display: inline-block !important;
          margin: 0 0.375rem !important;
        }

        /* Year/Month read view (the button that opens dropdown) - Enhanced */
        .react-datepicker__year-read-view,
        .react-datepicker__month-read-view {
          cursor: pointer !important;
          padding: 0.375rem 0.75rem !important;
          border-radius: 0.375rem !important;
          transition: all 0.2s ease-in-out !important;
          font-weight: 500 !important;
          border: 1px solid transparent !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 0.25rem !important;
        }

        /* Arrow icon inside read view */
        .react-datepicker__year-read-view--down-arrow,
        .react-datepicker__month-read-view--down-arrow {
          border-width: 5px 5px 0 5px !important;
          margin-left: 0.375rem !important;
          top: 0 !important;
          position: relative !important;
        }

        /* Smooth scrolling */
        .react-datepicker__year-dropdown--scrollable,
        .react-datepicker__month-dropdown {
          scroll-behavior: smooth !important;
        }

        /* Scrollbar styles for year dropdown - Light */
        .react-datepicker-light .react-datepicker__year-dropdown--scrollable::-webkit-scrollbar,
        .react-datepicker-light .react-datepicker__month-dropdown::-webkit-scrollbar {
          width: 6px;
        }

        .react-datepicker-light .react-datepicker__year-dropdown--scrollable::-webkit-scrollbar-track,
        .react-datepicker-light .react-datepicker__month-dropdown::-webkit-scrollbar-track {
          background: var(--bg-secondary);
          border-radius: 3px;
        }

        .react-datepicker-light .react-datepicker__year-dropdown--scrollable::-webkit-scrollbar-thumb,
        .react-datepicker-light .react-datepicker__month-dropdown::-webkit-scrollbar-thumb {
          background: var(--border-secondary);
          border-radius: 3px;
        }

        .react-datepicker-light .react-datepicker__year-dropdown--scrollable::-webkit-scrollbar-thumb:hover,
        .react-datepicker-light .react-datepicker__month-dropdown::-webkit-scrollbar-thumb:hover {
          background: var(--text-tertiary);
        }

        /* Scrollbar styles for year dropdown - Dark */
        .react-datepicker-dark .react-datepicker__year-dropdown--scrollable::-webkit-scrollbar,
        .react-datepicker-dark .react-datepicker__month-dropdown::-webkit-scrollbar,
        .dark .react-datepicker__year-dropdown--scrollable::-webkit-scrollbar,
        .dark .react-datepicker__month-dropdown::-webkit-scrollbar,
        [data-theme="dark"] .react-datepicker__year-dropdown--scrollable::-webkit-scrollbar,
        [data-theme="dark"] .react-datepicker__month-dropdown::-webkit-scrollbar {
          width: 6px;
        }

        .react-datepicker-dark .react-datepicker__year-dropdown--scrollable::-webkit-scrollbar-track,
        .react-datepicker-dark .react-datepicker__month-dropdown::-webkit-scrollbar-track,
        .dark .react-datepicker__year-dropdown--scrollable::-webkit-scrollbar-track,
        .dark .react-datepicker__month-dropdown::-webkit-scrollbar-track,
        [data-theme="dark"] .react-datepicker__year-dropdown--scrollable::-webkit-scrollbar-track,
        [data-theme="dark"] .react-datepicker__month-dropdown::-webkit-scrollbar-track {
          background: var(--bg-tertiary);
          border-radius: 3px;
        }

        .react-datepicker-dark .react-datepicker__year-dropdown--scrollable::-webkit-scrollbar-thumb,
        .react-datepicker-dark .react-datepicker__month-dropdown::-webkit-scrollbar-thumb,
        .dark .react-datepicker__year-dropdown--scrollable::-webkit-scrollbar-thumb,
        .dark .react-datepicker__month-dropdown::-webkit-scrollbar-thumb,
        [data-theme="dark"] .react-datepicker__year-dropdown--scrollable::-webkit-scrollbar-thumb,
        [data-theme="dark"] .react-datepicker__month-dropdown::-webkit-scrollbar-thumb {
          background: var(--border-primary);
          border-radius: 3px;
        }

        .react-datepicker-dark .react-datepicker__year-dropdown--scrollable::-webkit-scrollbar-thumb:hover,
        .react-datepicker-dark .react-datepicker__month-dropdown::-webkit-scrollbar-thumb:hover,
        .dark .react-datepicker__year-dropdown--scrollable::-webkit-scrollbar-thumb:hover,
        .dark .react-datepicker__month-dropdown::-webkit-scrollbar-thumb:hover,
        [data-theme="dark"] .react-datepicker__year-dropdown--scrollable::-webkit-scrollbar-thumb:hover,
        [data-theme="dark"] .react-datepicker__month-dropdown::-webkit-scrollbar-thumb:hover {
          background: var(--text-secondary);
        }
      ` } })] }));
}
export function TimeRangePicker({ label, error, helperText, required, className, darkMode, value = '', onChange, placeholder = "e.g., 09:00-17:00" }) {
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    // Parse initial value
    useEffect(() => {
        if (value && value.includes('-')) {
            const [start, end] = value.split('-');
            if (start && end) {
                const today = new Date();
                const startDate = new Date(today);
                const endDate = new Date(today);
                const [startHour, startMin] = start.trim().split(':');
                const [endHour, endMin] = end.trim().split(':');
                startDate.setHours(parseInt(startHour), parseInt(startMin), 0, 0);
                endDate.setHours(parseInt(endHour), parseInt(endMin), 0, 0);
                setStartTime(startDate);
                setEndTime(endDate);
            }
        }
        else {
            setStartTime(null);
            setEndTime(null);
        }
    }, [value]);
    const formatTime = (date) => {
        if (!date)
            return '';
        return date.toTimeString().slice(0, 5); // HH:MM
    };
    const handleTimeChange = (time, isStart) => {
        if (isStart) {
            setStartTime(time);
            if (time && endTime) {
                const range = `${formatTime(time)}-${formatTime(endTime)}`;
                onChange?.(range);
            }
        }
        else {
            setEndTime(time);
            if (startTime && time) {
                const range = `${formatTime(startTime)}-${formatTime(time)}`;
                onChange?.(range);
            }
        }
    };
    return (_jsxs("div", { className: cn("space-y-1", className), children: [label && (_jsxs("label", { className: "block text-sm font-medium text-gray-700 dark:text-gray-200", children: [label, required && _jsx("span", { className: "text-red-500 ml-1", children: "*" })] })), _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx(DatePicker, { variant: "time", value: startTime, onChange: (date) => handleTimeChange(date, true), darkMode: darkMode, placeholder: "Start time", className: "flex-1" }), _jsx("span", { className: "text-gray-500 dark:text-gray-400 text-sm", children: "to" }), _jsx(DatePicker, { variant: "time", value: endTime, onChange: (date) => handleTimeChange(date, false), darkMode: darkMode, placeholder: "End time", className: "flex-1" })] }), error && (_jsx("p", { className: "text-sm text-red-600 dark:text-red-400", children: error })), helperText && !error && (_jsx("p", { className: "text-sm text-gray-500 dark:text-gray-400", children: helperText }))] }));
}
export function DateRangePicker({ label, error, helperText, required, className, darkMode, startDate, endDate, onChange, placeholder = "Select date range", disabled = false, }) {
    // Auto-detect dark mode from document if not explicitly set
    const [isDarkMode, setIsDarkMode] = useState(darkMode ?? (typeof window !== 'undefined' && document.documentElement.classList.contains('dark')));
    // Listen for dark mode changes
    useEffect(() => {
        if (darkMode !== undefined) {
            setIsDarkMode(darkMode);
            return;
        }
        if (typeof window === 'undefined')
            return;
        const updateDarkMode = () => {
            setIsDarkMode(document.documentElement.classList.contains('dark'));
        };
        updateDarkMode();
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    updateDarkMode();
                }
            });
        });
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });
        return () => observer.disconnect();
    }, [darkMode]);
    const handleChange = (dates) => {
        onChange?.(dates);
    };
    return (_jsxs("div", { className: cn("w-full", label && "space-y-1", className), children: [label && (_jsxs("label", { className: "block text-sm font-medium text-gray-700 dark:text-gray-200", children: [label, required && _jsx("span", { className: "text-red-500 ml-1", children: "*" })] })), _jsx(ReactDatePicker, { selectsRange: true, startDate: startDate, endDate: endDate, onChange: handleChange, disabled: disabled, customInput: _jsx(CustomInput, { error: error, darkMode: isDarkMode, variant: "date" }), calendarClassName: cn("react-datepicker-custom", isDarkMode ? "react-datepicker-dark" : "react-datepicker-light"), popperClassName: cn("react-datepicker-popper-custom", isDarkMode ? "react-datepicker-popper-dark" : "react-datepicker-popper-light"), showPopperArrow: false, dateFormat: "dd/MM/yyyy", placeholderText: placeholder, isClearable: true, autoComplete: "off" }), error && (_jsx("p", { className: "text-sm text-red-600 dark:text-red-400", children: error })), helperText && !error && (_jsx("p", { className: "text-sm text-gray-500 dark:text-gray-400", children: helperText }))] }));
}
export default DatePicker;
